import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchCmsItemBySlug, fetchCmsItems, getOptimizedImageUrl } from '../lib/cms'
import ImageLightbox from '../components/ImageLightbox'
import InquiryModal from '../components/InquiryModal'

export default function TripDetailPage() {
  const { slug } = useParams()
  const [trip, setTrip] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    async function loadTrip() {
      setLoading(true)
      const data = await fetchCmsItemBySlug('rejser', slug)
      if (data) {
        setTrip(data)
        document.title = `${data.rejsenavn} - SKI & CO TRAVEL ApS`
      } else {
        document.title = 'Rejse ikke fundet - SKI & CO TRAVEL ApS'
      }
      setLoading(false)
    }
    loadTrip()
  }, [slug])

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-400">
        <i className="fa-solid fa-spinner animate-spin text-3xl text-cyan-400 mb-3" aria-hidden="true" />
        <p>Henter rejseoplysninger...</p>
      </div>
    )
  }

  if (!trip) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-slate-800 text-slate-400 flex items-center justify-center mx-auto text-2xl">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
        </div>
        <h1 className="font-heading font-extrabold text-3xl text-white">
          Rejsen blev ikke fundet
        </h1>
        <p className="text-slate-400 text-sm">
          Den efterspurgte skirejse findes ikke eller er udgået.
        </p>
        <Link
          to="/skirejser"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-sm transition-colors"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          <span>Tilbage til rejseoversigt</span>
        </Link>
      </div>
    )
  }

  const imageUrl = Array.isArray(trip.hero_billede)
    ? trip.hero_billede[0]
    : trip.hero_billede

  const galleryImages = [
    imageUrl,
    'https://assets.auxodev.dk/assets/9bff8fd4-e9da-49ba-96b9-18c20ed7b396/gallery-powder.png',
    'https://assets.auxodev.dk/assets/9bff8fd4-e9da-49ba-96b9-18c20ed7b396/hotel-spa-resort.png',
  ].filter(Boolean)

  return (
    <div className="space-y-12 pb-16">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <nav className="flex items-center gap-2 text-xs text-slate-400">
          <Link to="/" className="hover:text-cyan-400">Forside</Link>
          <span>/</span>
          <Link to="/skirejser" className="hover:text-cyan-400">Skirejser</Link>
          <span>/</span>
          <span className="text-slate-200 font-semibold">{trip.rejsenavn}</span>
        </nav>
      </div>

      {/* Hero Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative rounded-3xl overflow-hidden border border-slate-800 bg-slate-900 shadow-2xl">
          <div className="relative h-[400px] lg:h-[500px]">
            <img
              src={getOptimizedImageUrl(imageUrl, 1600, 85)}
              alt={trip.rejsenavn}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-transparent" />

            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              <span className="bg-slate-950/80 backdrop-blur-md text-cyan-300 text-xs font-semibold px-3 py-1 rounded-full border border-slate-700">
                {trip.land}
              </span>
              {trip.rejsetype && (
                <span className="bg-cyan-950/80 backdrop-blur-md text-cyan-300 text-xs font-medium px-3 py-1 rounded-full border border-cyan-800/60">
                  {trip.rejsetype}
                </span>
              )}
            </div>

            <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 lg:right-10 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-cyan-400">
                {trip.destination}
              </span>
              <h1 className="font-heading font-extrabold text-2xl sm:text-4xl text-white">
                {trip.rejsenavn}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid: Content + Booking Box */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Main Info */}
          <div className="lg:col-span-8 space-y-8">
            {/* Short & Long Description */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="font-heading font-bold text-xl text-white">
                Om rejsen
              </h2>
              <p className="text-slate-300 text-base leading-relaxed font-medium">
                {trip.kort_beskrivelse}
              </p>

              {trip.lang_beskrivelse && (
                <div
                  className="prose prose-invert prose-cyan text-slate-300 text-sm leading-relaxed pt-4 border-t border-slate-800"
                  dangerouslySetInnerHTML={{ __html: trip.lang_beskrivelse }}
                />
              )}
            </div>

            {/* Inclusions */}
            {trip.inkluderet && (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4">
                <h3 className="font-heading font-bold text-xl text-white flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-cyan-400" aria-hidden="true" />
                  <span>Dette er inkluderet i prisen</span>
                </h3>
                <div
                  className="prose prose-invert text-slate-300 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: trip.inkluderet }}
                />
              </div>
            )}

            {/* Trip-specific Practical Info */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4">
              <h3 className="font-heading font-bold text-xl text-white flex items-center gap-2">
                <i className="fa-solid fa-clipboard-list text-cyan-400" aria-hidden="true" />
                <span>Praktisk information for denne rejse</span>
              </h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Bemærk, at praktisk information varierer fra rejse til rejse. For denne tur gælder følgende nøglepunkter:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-1">
                  <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider block">Transport & Afrejse</span>
                  <span className="text-slate-200 text-sm font-medium block">{trip.transport || 'Oplyses ved tilbud'}</span>
                </div>
                <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-1">
                  <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider block">Indkvartering</span>
                  <span className="text-slate-200 text-sm font-medium block">{trip.hotel || 'Spørg for detaljer'}</span>
                </div>
                <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-1">
                  <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider block">Varighed</span>
                  <span className="text-slate-200 text-sm font-medium block">{trip.varighed || '7 nætter'}</span>
                </div>
                <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-1">
                  <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider block">Destination & Land</span>
                  <span className="text-slate-200 text-sm font-medium block">{trip.destination} ({trip.land})</span>
                </div>
              </div>
              <p className="text-xs text-slate-400 pt-2">
                Har du spørgsmål til særlige ønsker, bagageregler eller skileje på denne tur? <a href="/kontakt" className="text-cyan-400 hover:underline font-semibold">Kontakt os for personlig rådgivning</a>.
              </p>
            </div>

            {/* Photo Gallery Thumbnails */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sm:p-8 space-y-4">
              <h3 className="font-heading font-bold text-xl text-white">
                Billeder fra rejsen
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setLightboxIndex(idx)
                      setLightboxOpen(true)
                    }}
                    className="relative h-28 rounded-xl overflow-hidden border border-slate-800 hover:border-cyan-400 transition-all group"
                  >
                    <img
                      src={getOptimizedImageUrl(img, 400, 80)}
                      alt={`Billede ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                    />
                    <div className="absolute inset-0 bg-slate-950/20 group-hover:bg-transparent transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Booking & Facts Box */}
          <div className="lg:col-span-4 space-y-6">
            <div className="sticky top-28 bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-6 shadow-2xl">
              <div className="pb-4 border-b border-slate-800">
                <span className="text-xs text-slate-400 block">Pris pr. person fra</span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="font-heading font-extrabold text-4xl text-white">
                    {trip.pris ? trip.pris.toLocaleString('da-DK') : 'TBA'}
                  </span>
                  <span className="text-sm font-bold text-cyan-400">DKK</span>
                </div>
                {trip.prisbeskrivelse && (
                  <p className="text-xs text-slate-400 mt-2">
                    {trip.prisbeskrivelse}
                  </p>
                )}
              </div>

              {/* Key Specs */}
              <div className="space-y-3 text-sm text-slate-300">
                {trip.afrejsedato && (
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/60">
                    <span className="text-slate-400 flex items-center gap-2">
                      <i className="fa-regular fa-calendar-days text-cyan-400" aria-hidden="true" />
                      <span>Afrejsedato:</span>
                    </span>
                    <span className="font-semibold text-white">{trip.afrejsedato}</span>
                  </div>
                )}

                {trip.varighed && (
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/60">
                    <span className="text-slate-400 flex items-center gap-2">
                      <i className="fa-regular fa-clock text-cyan-400" aria-hidden="true" />
                      <span>Varighed:</span>
                    </span>
                    <span className="font-semibold text-white">{trip.varighed}</span>
                  </div>
                )}

                {trip.hotel && (
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/60">
                    <span className="text-slate-400 flex items-center gap-2">
                      <i className="fa-solid fa-hotel text-cyan-400" aria-hidden="true" />
                      <span>Hotel:</span>
                    </span>
                    <span className="font-semibold text-white truncate max-w-[160px]">{trip.hotel}</span>
                  </div>
                )}

                {trip.transport && (
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/60">
                    <span className="text-slate-400 flex items-center gap-2">
                      <i className="fa-solid fa-bus text-cyan-400" aria-hidden="true" />
                      <span>Transport:</span>
                    </span>
                    <span className="font-semibold text-white">{trip.transport}</span>
                  </div>
                )}

                {trip.ledighed && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-slate-400 flex items-center gap-2">
                      <i className="fa-solid fa-user-check text-cyan-400" aria-hidden="true" />
                      <span>Status:</span>
                    </span>
                    <span className="font-bold text-amber-400">{trip.ledighed}</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <button
                  onClick={() => setModalOpen(true)}
                  className="w-full py-3.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-base transition-colors shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                  <span>Send forespørgsel</span>
                </button>

                <a
                  href="tel:+4551589452"
                  className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-750 text-white font-semibold text-sm transition-colors border border-slate-700 flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                  <span>Ring på +45 51 58 94 52</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      <ImageLightbox
        images={galleryImages}
        currentIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : galleryImages.length - 1))}
        onNext={() => setLightboxIndex((prev) => (prev < galleryImages.length - 1 ? prev + 1 : 0))}
      />

      {/* Inquiry Modal */}
      <InquiryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedTrip={trip.rejsenavn}
        selectedDestination={trip.destination}
      />
    </div>
  )
}
